import { Fragment, useMemo, useState } from 'react';
import type { WorkForecast } from '@catalyst-psa/shared';

import { PageHeader } from '../../components/layout/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useWorkForecast } from './api.js';

/** Formats a 'YYYY-MM' month key as e.g. "Jul 2026". */
function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** A drill-down opportunity row within a type. */
interface OppRow {
  opportunityId: string;
  opportunityName: string;
  customerName: string;
  month: string;
}

/**
 * Upcoming Work Forecast (Delivery): a count of open opportunities (future projects)
 * broken down by Type (the opportunity's own service category, rows) and close month
 * (columns). Mirrors the sales Pipeline Forecast but counts projects instead of revenue.
 * Clicking a Type row drills down to its contributing opportunities under the month they
 * close in.
 */
export function WorkForecastPage(): JSX.Element {
  const query = useWorkForecast();
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  const forecast: WorkForecast | undefined = query.data;
  const months = forecast?.months ?? [];

  const toggleType = (type: string): void => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Contributions grouped per type by opportunity so a drill-down row spans the months.
  const oppsByType = useMemo(() => {
    const map = new Map<string, OppRow[]>();
    for (const type of forecast?.types ?? []) {
      const rows = [...type.contributions].sort((a, b) =>
        a.month === b.month
          ? a.opportunityName.localeCompare(b.opportunityName)
          : a.month.localeCompare(b.month),
      );
      map.set(type.type, rows);
    }
    return map;
  }, [forecast]);

  return (
    <div>
      <PageHeader
        title="Work Forecast"
        description="A count of open opportunities (upcoming projects) by type and expected close month. Helps the delivery team see how much work is coming and when."
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {query.isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500">
            <Spinner className="h-4 w-4" /> Loading forecast…
          </div>
        ) : query.isError ? (
          <div className="px-4 py-6 text-sm text-rose-600">Failed to load the forecast.</div>
        ) : !forecast || forecast.types.length === 0 ? (
          <div className="px-4 py-6 text-sm text-slate-500">
            No upcoming work yet. Open opportunities need a close date to appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="sticky left-0 z-10 bg-white px-4 py-2 font-medium">Type</th>
                  {months.map((m) => (
                    <th key={m} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                      {formatMonth(m)}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {forecast.types.map((type) => {
                  const isOpen = expandedTypes.has(type.type);
                  const oppRows = oppsByType.get(type.type) ?? [];
                  return (
                    <Fragment key={type.type}>
                      <tr
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleType(type.type)}
                      >
                        <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-slate-900">
                          <span className="inline-flex items-center gap-1.5">
                            <svg
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.21 4.21a.75.75 0 011.06 0l5 5a.75.75 0 010 1.06l-5 5a.75.75 0 11-1.06-1.06L11.69 10 7.21 5.27a.75.75 0 010-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {type.type}
                          </span>
                        </td>
                        {months.map((m) => (
                          <td
                            key={m}
                            className="whitespace-nowrap px-3 py-2 text-right text-slate-600"
                          >
                            {type.countsByMonth[m] ? type.countsByMonth[m] : '—'}
                          </td>
                        ))}
                        <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-slate-900">
                          {type.total}
                        </td>
                      </tr>
                      {isOpen
                        ? oppRows.map((row) => (
                            <tr
                              key={`${type.type}|${row.opportunityId}|${row.month}`}
                              className="bg-slate-50/40"
                            >
                              <td className="sticky left-0 z-10 bg-inherit px-4 py-1.5 pl-9 text-slate-600">
                                <span className="font-medium text-slate-700">
                                  {row.opportunityName}
                                </span>
                                <span className="text-slate-400"> · {row.customerName}</span>
                              </td>
                              {months.map((m) => (
                                <td
                                  key={m}
                                  className="whitespace-nowrap px-3 py-1.5 text-right text-slate-500"
                                >
                                  {row.month === m ? 1 : ''}
                                </td>
                              ))}
                              <td className="whitespace-nowrap px-4 py-1.5 text-right text-slate-600">
                                1
                              </td>
                            </tr>
                          ))
                        : null}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-900">
                <tr>
                  <td className="sticky left-0 z-10 bg-white px-4 py-2">Total</td>
                  {months.map((m) => (
                    <td key={m} className="whitespace-nowrap px-3 py-2 text-right">
                      {forecast.totalsByMonth[m] ? forecast.totalsByMonth[m] : '—'}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-4 py-2 text-right">{forecast.total}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
